import classnames from 'classnames';
import { arrayOf, bool, shape } from 'prop-types';
import React, { useState, useRef } from 'react';

import Fancybox from '@/components/FancyBox';
import Modal from '@/components/Modal/Modal';
import { PortableText } from '@/components/PortableText/PortableText';
import LazyYouTube from '@/components/Youtube';
import {
  ResponsiveImage,
  getResponsiveImageUrl,
} from '@/components/images/ResponsiveImage';

import { Text } from '../../module/Text';
import LightBox from './components/LightBox';

import styles from './GallerySimple.module.scss';

const GallerySimple = ({
  video,
  images,
  thumbnails = false,
  isFullWidth = true,
  description = false,
  videoDescription = false,
  collapseBorders = false,
}) => {
  const blockRef = useRef(null);
  const itemEls = useRef([]);

  const [isShowModal, setIsShowModal] = useState(false);

  const wrapperClasses = classnames(styles.wrapper, {
    [styles.fullWidth]: isFullWidth,
    [styles.singleLayout]: !description,
    [styles.simpleGrid]: video?.youtube?.url ? images.length < 5 : images.length < 6,
    [styles.collapseBorders]: collapseBorders,
  });

  const handleClick = (e) => {
    setIsShowModal((showModal) => !showModal);
  };

  return (
    <div className={wrapperClasses}>
      <div className={styles.container}>
        {video?.youtube?.url && (
          <div className={styles.video} onClick={handleClick}>
            <LazyYouTube url={video.youtube.url} autoPlay loop background />
          </div>
        )}
        <Fancybox
          options={{
            mainClass: `${styles.galleryBox} ${
              !description ? styles.singleLayout : styles.descriptionLayout
            }`,
            Thumbs: {
              autoStart: thumbnails, // Display thumbnails on opening
              hideOnClose: true, // Hide thumbnail grid when closing animation starts
            },
            Toolbar: {
              display: [
                { id: 'prev', position: 'left' },
                { id: 'counter', position: 'left' },
                { id: 'next', position: 'left' },
                'zoom',
                'slideshow',
                'thumbs',
                'close',
              ],
            },
            preload: 0,
            Image: {
              wheel: null,
            },
            on: {
              initLayout: (fancybox) => {
                if (description) {
                  const $mainPanel = document.createElement('div');
                  $mainPanel.classList.add('fancybox__main-panel');

                  const $leftPanel = document.createElement('div');
                  $leftPanel.classList.add('fancybox__left-panel');
                  const $rightPanel = document.createElement('div');
                  $rightPanel.classList.add('fancybox__right-panel');
                  $leftPanel.innerHTML =
                    document.getElementById('gallery-data').innerHTML;
                  $rightPanel.appendChild(fancybox.$carousel);
                  $mainPanel.appendChild($rightPanel);
                  $mainPanel.appendChild($leftPanel);
                  fancybox.$backdrop.after($mainPanel);
                }
              },
              'Carousel.change': (fancybox, carousel, slide) => {
                if (description) {
                  document
                    .querySelector('.fancybox__container .block.activeContent')
                    .classList.remove('activeContent');
                  document
                    .querySelector(
                      `.fancybox__container .block[data-index="${slide}"]`,
                    )
                    .classList.add('activeContent');
                }
              },
              initCarousel: (fancybox, carousel, slide) => {
                if (description) {
                  document
                    .querySelector(
                      `.fancybox__container .block[data-index="${fancybox.Carousel.pageIndex}"]`,
                    )
                    .classList.add('activeContent');
                }
              },
              shouldClose: () => {
                if (description) {
                  document
                    .querySelector('.fancybox__container .block.activeContent')
                    .classList.remove('activeContent');
                }
              },
            },
          }}
        >
          {images
            .filter(({ image }) => Boolean(image))
            .map((el, idx) => {
              const galleryImage = getResponsiveImageUrl({
                asset: el.image.asset,
              });

              return (
                <div
                  key={idx}
                  className={styles.image}
                  data-fancybox="gallery"
                  data-src={galleryImage}
                  ref={(element) => (itemEls.current[idx] = element)}
                >
                  <ResponsiveImage
                    asset={el.image.asset}
                    hotspot={el.image.hotspot}
                    alt="image"
                    layout="fill"
                  />
                </div>
              );
            })}
        </Fancybox>
      </div>

      {description && (
        <div id="gallery-data" className={styles.hidden}>
          {images.map((item, ind) => {
            return (
              <div
                ref={blockRef}
                className={`${styles.content} block`}
                key={ind}
                data-index={ind}
              >
                {item.description && (
                  <Text className={styles.markdown}>
                    <PortableText content={item.description} />
                  </Text>
                )}
              </div>
            );
          })}
        </div>
      )}

      {isShowModal && (
        <Modal handleClick={handleClick}>
          <LightBox video={video} description={videoDescription} />
        </Modal>
      )}
    </div>
  );
};

GallerySimple.propTypes = {
  video: shape({}),
  images: arrayOf(shape({})),
  thumbnails: bool,
  isFullWidth: bool,
  description: bool,
  videoDescription: bool,
  collapseBorders: bool,
};

GallerySimple.defaultProps = {
  video: {},
  images: [{}],
  thumbnails: false,
  isFullWidth: true,
  description: false,
  videoDescription: false,
  collapseBorders: false,
};

export default GallerySimple;
